/**
 * Import Modal Type Definitions
 *
 * Type definitions for the multi-stage import modal system.
 * Supports file upload, validation, and preview workflows.
 *
 * @module components/import/types
 */

/**
 * Import stage enumeration
 */
export enum ImportStage {
  SELECT_SOURCE = 'select_source',
  UPLOAD_FILE = 'upload_file',
  VALIDATE = 'validate',
}

/**
 * File validation result
 */
export interface ValidationResult {
  /** Whether the file is valid */
  valid: boolean;
  /** Validation error message (if invalid) */
  error?: string;
  /** Preview data (first N rows) */
  preview?: PreviewData;
  /** File metadata */
  metadata?: {
    rowCount?: number;
    columnCount?: number;
    fileSize: number;
    fileName: string;
  };
}

/**
 * Preview data structure
 */
export interface PreviewData {
  /** Preview rows (array of objects or string lines) */
  rows: unknown[];
  /** Column headers (if applicable) */
  headers?: string[];
  /** Total row count in file */
  totalRows?: number;
}

/**
 * Accepted file type configuration for dropzone
 */
export interface AcceptedFileTypes {
  [mimeType: string]: string[];
}

/**
 * Configuration option for import actions
 * Enables capability-specific settings like auto-approve
 */
export interface ImportConfigOption {
  /** Unique identifier for config option (e.g., 'auto_approve') */
  key: string;
  /** Input type for the configuration */
  type: 'checkbox' | 'text' | 'select';
  /** Display label */
  label: string;
  /** Help text describing the option */
  description: string;
  /** Default value when unselected */
  defaultValue: boolean | string;
  /** Options for 'select' type (label-value pairs) */
  options?: Array<{ label: string; value: string }>;
}

/**
 * Import source action definition (UPDATED with validation config)
 */
export interface ImportAction {
  /** Display label for the import source */
  label: string;
  /** Unique identifier for the source */
  value: string;
  /** Optional description of the import source */
  description?: string;
  /** Whether this option is disabled */
  disabled?: boolean;

  /** Validation configuration */
  validation?: {
    /** Whether to show validation preview stage */
    enabled: boolean;
    /** Number of rows/lines to preview (default: 10) */
    previewRows?: number;
    /** Custom validation function (optional) */
    validate?: (file: File) => Promise<ValidationResult>;
  };

  /** Accepted file types for dropzone (MIME type -> extensions mapping) */
  acceptedFileTypes?: AcceptedFileTypes;
  /** Human-readable list of accepted formats (e.g., "CSV, JSON") */
  acceptedFormatsLabel?: string;

  /** Optional capability-specific configuration options */
  configs?: ImportConfigOption[];
}

/**
 * Import modal state
 */
export interface ImportModalState {
  currentStage: ImportStage;
  selectedAction: ImportAction | null;
  uploadedFile: File | null;
  uploadedFilePath: string | null; // S3 path after upload
  validationResult: ValidationResult | null;
  isValidating: boolean;
  isUploading: boolean; // Track S3 upload progress
}
